import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type PayableStatus = 'paid' | 'overdue' | 'partial' | 'open';
export type PayableFilter = 'open' | 'overdue' | 'paid';
export type PayableMethod =
  | 'pix'
  | 'boleto'
  | 'cash'
  | 'bank_transfer'
  | 'credit_card'
  | 'debit_card'
  | 'other';

export interface Payable {
  id: string;
  supplierId: string;
  supplierName: string;
  supplierPhone: string | null;
  purchaseOrderId: string | null;
  purchaseOrderNumber: number | null;
  description: string;
  amount: number;
  paidAmount: number;
  remaining: number;
  dueDate: string | null;
  status: PayableStatus;
  notes: string | null;
  createdAt: string;
}

export interface PayablePayment {
  id: string;
  amount: number;
  paidAt: string;
  method: PayableMethod | null;
  notes: string | null;
  createdAt: string;
}

export interface PayablesSummary {
  openTotal: number;
  overdueTotal: number;
  dueIn7DaysTotal: number;
  openCount: number;
  overdueCount: number;
  bySupplier: {
    supplierId: string;
    supplierName: string;
    openTotal: number;
    overdueTotal: number;
    openCount: number;
  }[];
}

export const PAYABLE_STATUS_LABELS: Record<PayableStatus, string> = {
  paid: 'Pago',
  overdue: 'Vencido',
  partial: 'Parcial',
  open: 'Em aberto',
};

export const PAYABLE_METHOD_LABELS: Record<PayableMethod, string> = {
  pix: 'PIX',
  boleto: 'Boleto',
  cash: 'Dinheiro',
  bank_transfer: 'Transferência',
  credit_card: 'Cartão de crédito',
  debit_card: 'Cartão de débito',
  other: 'Outro',
};

interface PayablesListResponse {
  items: Payable[];
  total: number;
  openTotal: number;
  overdueTotal: number;
}

export function usePayables(filters: { status?: PayableFilter; supplierId?: string } = {}) {
  const queryClient = useQueryClient();
  const { status, supplierId } = filters;

  // Uma baixa muda o saldo do fornecedor e o KPI do dashboard — invalidar só a
  // lista deixaria as duas outras telas mostrando o valor velho.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['payables'] });
    queryClient.invalidateQueries({ queryKey: ['payables-summary'] });
    queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const listQuery = useQuery<PayablesListResponse>({
    queryKey: ['payables', status ?? null, supplierId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (supplierId) params.set('supplierId', supplierId);
      const qs = params.toString();
      const res = await api.get(`/workshops/my/finance/payables${qs ? `?${qs}` : ''}`);
      return res.data;
    },
  });

  const summaryQuery = useQuery<PayablesSummary>({
    queryKey: ['payables-summary'],
    queryFn: async () => (await api.get('/workshops/my/finance/payables/summary')).data,
  });

  const createMutation = useMutation({
    mutationFn: async (data: {
      supplierId: string;
      description: string;
      amount: number;
      dueDate?: string | null;
      notes?: string | null;
    }) => (await api.post('/workshops/my/finance/payables', data)).data as Payable,
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      supplierId?: string;
      description?: string;
      amount?: number;
      dueDate?: string | null;
      notes?: string | null;
    }) => (await api.patch(`/workshops/my/finance/payables/${id}`, data)).data as Payable,
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(`/workshops/my/finance/payables/${id}`)).data,
    onSuccess: invalidate,
  });

  const payMutation = useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      amount: number;
      paidAt?: string;
      method?: PayableMethod | null;
      notes?: string | null;
    }) => (await api.post(`/workshops/my/finance/payables/${id}/payments`, data)).data as Payable,
    onSuccess: invalidate,
  });

  const reversePaymentMutation = useMutation({
    mutationFn: async ({ id, paymentId }: { id: string; paymentId: string }) =>
      (await api.delete(`/workshops/my/finance/payables/${id}/payments/${paymentId}`)).data,
    onSuccess: invalidate,
  });

  return {
    payables: listQuery.data?.items ?? [],
    total: listQuery.data?.total ?? 0,
    openTotal: listQuery.data?.openTotal ?? 0,
    overdueTotal: listQuery.data?.overdueTotal ?? 0,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error,
    refetch: listQuery.refetch,
    summary: summaryQuery.data,
    isLoadingSummary: summaryQuery.isLoading,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    remove: removeMutation.mutateAsync,
    isRemoving: removeMutation.isPending,
    pay: payMutation.mutateAsync,
    isPaying: payMutation.isPending,
    reversePayment: reversePaymentMutation.mutateAsync,
    isReversing: reversePaymentMutation.isPending,
  };
}

/** Detalhe de um título com o extrato de baixas. */
export function usePayableDetail(id: string | null) {
  return useQuery<Payable & { payments: PayablePayment[] }>({
    queryKey: ['payable', id],
    enabled: !!id,
    queryFn: async () => (await api.get(`/workshops/my/finance/payables/${id}`)).data,
  });
}
